import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { Autocomplete, TextField } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import DataGridTable from "../Components/DataGridTable";
import { useAuth } from "../hooks/AuthProvider";
import {
  getLatestPeopleCounterStatistics,
  getManyPeopleCounterStatistics,
  getOnePeopleCounterStatistics,
  getPeopleCounterDeviceOptions,
  getPeopleCounterOrganizations,
} from "../api/peopleCounterApi";
import {
  buildPeopleCounterChartData,
  buildPeopleCounterSummary,
  normalizePeopleCounterRows,
} from "../utils/peopleCounterStats";
import PeopleCounterLayout, { usePeopleCounterTranslations } from "./PeopleCounterLayout";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.INFO || error?.message || fallback;

export default function PeopleCounterStatistics() {
  const { org, permissions } = useAuth();
  const canViewStatistics = Boolean(permissions?.view_people_counter_statistics);
  const { t, lang } = usePeopleCounterTranslations();
  const [organizations, setOrganizations] = useState([]);
  const [organizationsReady, setOrganizationsReady] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [mode, setMode] = useState("latest");
  const [rows, setRows] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingStatistics, setLoadingStatistics] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [devicesScopeKey, setDevicesScopeKey] = useState(null);
  const deviceSequence = useRef(0);
  const statisticsSequence = useRef(0);

  const locale = lang === "ar" ? "ar-AE" : lang === "ur" ? "ur-PK" : "en-GB";
  const organizationScopeKey = selectedOrganization?.id
    ? String(selectedOrganization.id)
    : "all";
  const allOrganizationsOption = useMemo(
    () => ({ id: null, name: t.allOrganizations, isAll: true }),
    [t.allOrganizations]
  );
  const organizationOptions = useMemo(
    () => [allOrganizationsOption, ...organizations],
    [allOrganizationsOption, organizations]
  );
  const selectedOrganizationOption = selectedOrganization || allOrganizationsOption;

  useEffect(() => {
    if (!canViewStatistics) return undefined;
    let active = true;
    setOrganizationsReady(false);
    getPeopleCounterOrganizations()
      .then((response) => {
        if (!active) return;
        const options = Array.isArray(response.data?.data) ? response.data.data : [];
        const currentOrganization = options.find((item) => Number(item.id) === Number(org?.id));
        setOrganizations(options);
        setSelectedOrganization(currentOrganization || null);
        setOrganizationsReady(true);
      })
      .catch((requestError) => {
        if (!active) return;
        setOrganizations([]);
        setSelectedOrganization(null);
        setOrganizationsReady(true);
        setError(getErrorMessage(requestError, t.loadOrganizationsFailed));
      });
    return () => { active = false; };
  }, [canViewStatistics, org?.id, t.loadOrganizationsFailed]);

  const loadDevices = useCallback(async () => {
    if (!canViewStatistics || !organizationsReady) return;
    const sequence = ++deviceSequence.current;
    const requestedScopeKey = organizationScopeKey;
    setDevicesScopeKey(null);
    setLoadingDevices(true);
    setError("");
    try {
      const response = await getPeopleCounterDeviceOptions({
        ...(selectedOrganization?.id ? { organizationId: selectedOrganization.id } : {}),
      });
      if (sequence !== deviceSequence.current) return;
      const nextDevices = Array.isArray(response.data?.data) ? response.data.data : [];
      setDevices(nextDevices);
      setSelectedDevice((current) => {
        const matching = nextDevices.find(
          (device) => Number(device.people_counter_id) === Number(current?.people_counter_id)
        );
        return matching || nextDevices[0] || null;
      });
      setDevicesScopeKey(requestedScopeKey);
    } catch (requestError) {
      if (sequence !== deviceSequence.current) return;
      setDevices([]);
      setSelectedDevice(null);
      setError(getErrorMessage(requestError, t.loadDevicesFailed));
    } finally {
      if (sequence === deviceSequence.current) setLoadingDevices(false);
    }
  }, [canViewStatistics, organizationScopeKey, organizationsReady, selectedOrganization?.id, t.loadDevicesFailed]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const loadStatistics = useCallback(async (signal) => {
    if (!canViewStatistics || !organizationsReady) return;
    if (loadingDevices || devicesScopeKey !== organizationScopeKey) return;
    if (!devices.length) {
      setRows([]);
      setLastUpdated(new Date());
      return;
    }
    if (mode === "one" && !selectedDevice?.people_counter_id) {
      setRows([]);
      setLastUpdated(null);
      return;
    }

    const sequence = ++statisticsSequence.current;
    setLoadingStatistics(true);
    setError("");
    try {
      const payload = {
        ...(selectedOrganization?.id ? { organizationId: selectedOrganization.id } : {}),
      };
      const response = mode === "one"
        ? await getOnePeopleCounterStatistics(selectedDevice.people_counter_id, { signal })
        : mode === "many"
          ? await getManyPeopleCounterStatistics(payload, { signal })
          : await getLatestPeopleCounterStatistics(payload, { signal });

      if (sequence !== statisticsSequence.current) return;
      setRows(normalizePeopleCounterRows(response.data?.data));
      setLastUpdated(new Date());
    } catch (requestError) {
      if (sequence !== statisticsSequence.current || requestError?.code === "ERR_CANCELED") return;
      setRows([]);
      setError(getErrorMessage(requestError, t.loadStatisticsFailed));
    } finally {
      if (sequence === statisticsSequence.current) setLoadingStatistics(false);
    }
  }, [canViewStatistics, devices, devicesScopeKey, loadingDevices, mode, organizationScopeKey, organizationsReady, selectedDevice?.people_counter_id, selectedOrganization?.id, t.loadStatisticsFailed]);

  useEffect(() => {
    const controller = new AbortController();
    loadStatistics(controller.signal);
    return () => controller.abort();
  }, [loadStatistics, refreshKey]);

  const summary = useMemo(
    () => buildPeopleCounterSummary(rows, devices.length),
    [devices.length, rows]
  );
  const chartData = useMemo(() => buildPeopleCounterChartData(rows), [rows]);
  const columns = useMemo(() => [
    { field: "people_counter_name", headerName: t.deviceName, minWidth: 180, flex: 1 },
    { field: "mac_address", headerName: t.macAddress, minWidth: 190, flex: 1 },
    { field: "organization_name", headerName: t.organization, minWidth: 170, flex: 1 },
    { field: "line", headerName: t.line, width: 90, align: "center", headerAlign: "center" },
    { field: "line_name", headerName: t.lineName, minWidth: 150, flex: 1 },
    { field: "traffic_entered", headerName: t.entered, width: 110, type: "number" },
    { field: "traffic_exited", headerName: t.exited, width: 110, type: "number" },
    { field: "children_in", headerName: t.childrenIn, width: 120, type: "number" },
    { field: "children_out", headerName: t.childrenOut, width: 125, type: "number" },
    { field: "group_in", headerName: t.groupIn, width: 110, type: "number" },
    { field: "group_out", headerName: t.groupOut, width: 115, type: "number" },
    { field: "staff_in", headerName: t.staffIn, width: 105, type: "number" },
    { field: "staff_out", headerName: t.staffOut, width: 110, type: "number" },
    {
      field: "window_start",
      headerName: t.windowStart,
      minWidth: 185,
      flex: 1,
      valueFormatter: (value) => value ? new Date(value).toLocaleString(locale) : "-",
    },
    {
      field: "window_end",
      headerName: t.windowEnd,
      minWidth: 185,
      flex: 1,
      valueFormatter: (value) => value ? new Date(value).toLocaleString(locale) : "-",
    },
    {
      field: "received_at",
      headerName: t.readingTime,
      minWidth: 190,
      flex: 1,
      type: "dateTime",
      valueGetter: (value) => {
        if (!value) return null;
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
      },
      valueFormatter: (value) =>
        value instanceof Date && !Number.isNaN(value.getTime())
          ? value.toLocaleString(locale)
          : "-",
    },
  ], [locale, t]);

  const summaryCards = [
    { label: t.registeredDevices, value: summary.registeredDevices, icon: DevicesOtherOutlinedIcon },
    { label: t.reportingDevices, value: summary.reportingDevices, icon: SensorsOutlinedIcon },
    { label: t.currentOccupancy, value: summary.currentOccupancy, icon: GroupsOutlinedIcon },
  ];

  return (
    <PeopleCounterLayout title={t.statisticsTitle} description={t.statisticsDescription} requiredPermission="view_people_counter_statistics">
      <section className="people-counter-card people-counter-stats-controls">
        <Autocomplete
          className="people-counter-field"
          options={organizationOptions}
          value={selectedOrganizationOption}
          loading={!organizationsReady}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
          onChange={(_event, value) => setSelectedOrganization(value?.isAll ? null : value)}
          renderInput={(params) => <TextField {...params} size="small" label={t.filterOrganization} />}
        />
        <div className="people-counter-mode-control">
          <span className="people-counter-control-label">{t.dataMode}</span>
          <div className="people-counter-mode-switch" role="radiogroup" aria-label={t.dataMode}>
            {[
              ["latest", t.latestMode],
              ["many", t.historyMode],
              ["one", t.singleMode],
            ].map(([value, label]) => (
              <button
                type="button"
                role="radio"
                aria-checked={mode === value}
                className={mode === value ? "active" : ""}
                key={value}
                onClick={() => setMode(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {mode === "one" ? (
          <Autocomplete
            className="people-counter-field people-counter-device-filter"
            loading={loadingDevices}
            options={devices}
            value={selectedDevice}
            getOptionLabel={(option) => `${option?.name || ""} — ${option?.mac_address || ""}`}
            isOptionEqualToValue={(option, value) => Number(option.people_counter_id) === Number(value.people_counter_id)}
            onChange={(_event, value) => setSelectedDevice(value)}
            renderInput={(params) => <TextField {...params} size="small" label={t.device} />}
          />
        ) : null}
        <button
          type="button"
          className="devices-secondary-btn people-counter-button people-counter-refresh"
          onClick={() => setRefreshKey((value) => value + 1)}
          disabled={loadingStatistics || loadingDevices || !devices.length}
        >
          <RefreshIcon fontSize="small" /> <span>{t.refresh}</span>
        </button>
        <span className="people-counter-last-updated">
          {lastUpdated ? `${t.lastUpdated}: ${lastUpdated.toLocaleString(locale)}` : t.notUpdatedYet}
        </span>
      </section>

      {error ? <div className="people-counter-error">{error}</div> : null}

      <section className="people-counter-summary">
        {summaryCards.map(({ label, value, icon: Icon }) => (
          <article className="people-counter-card people-counter-summary-card" key={label}>
            <div className="people-counter-summary-icon"><Icon aria-hidden="true" /></div>
            <div className="people-counter-summary-content">
              <span>{label}</span>
              <strong>{Number(value || 0).toLocaleString(locale)}</strong>
            </div>
          </article>
        ))}
      </section>

      <section className="people-counter-visuals">
        <article className="people-counter-card people-counter-chart">
          <h2>{t.deviceTotalsChart}</h2>
          {chartData.length ? (
            <div className="people-counter-chart-canvas">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 12, right: 18, left: -8, bottom: 24 }} barCategoryGap="28%">
                  <CartesianGrid
                    stroke="var(--devices-grid-cell-border-strong)"
                    strokeDasharray="3 5"
                    vertical={false}
                  />
                <XAxis
                  dataKey="name"
                  angle={-16}
                  textAnchor="end"
                  height={54}
                  tick={{ fill: "var(--dg-toolbar-label)", fontSize: 11 }}
                  axisLine={{ stroke: "var(--devices-grid-cell-border-strong)" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "var(--dg-toolbar-label)", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <ChartTooltip
                  cursor={false}
                  contentStyle={{
                    background: "var(--dashboard-modal-bg)",
                    border: "1px solid var(--devices-grid-cell-border-strong)",
                    borderRadius: 10,
                    color: "var(--cell-color)",
                  }}
                />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ paddingTop: 8, fontSize: 12 }} />
                <Bar dataKey="peopleIn" name={t.entered} fill="#16c8a8" radius={[5, 5, 0, 0]} maxBarSize={58} />
                  <Bar dataKey="peopleOut" name={t.exited} fill="#3f8cff" radius={[5, 5, 0, 0]} maxBarSize={58} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <div className="people-counter-empty">{t.noStatistics}</div>}
        </article>

        <article className="people-counter-card people-counter-readings">
          <h2>{t.readingsTable}</h2>
          <div className="devices-grid-wrapper people-counter-readings-grid">
            <DataGridTable
              columns={columns}
              rows={rows}
              idcolumn="_row_id"
              pagingoptions={[10, 20, 50, 100]}
              sortField="received_at"
              sortType="desc"
              tableKey="people_counter_statistics"
              loading={loadingStatistics}
              showToolbar
            />
          </div>
        </article>
      </section>
    </PeopleCounterLayout>
  );
}
